import { gql } from "generated-graphql";
import {
  ManifestReviewDiscrepanciesQuery,
  StaffManifestReviewManifestsQuery,
} from "generated-graphql/graphql";

export const STAFF_MANIFEST_REVIEW_MANIFESTS_QUERY = gql(`
  query StaffManifestReviewManifests($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    staffManifestReviewManifests(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      totalCount
      items {
        id
        processedManifestId
        tenantId
        tenantName
        facilityName
        facilityEpaId
        manifestTrackingNumber
        discrepancyCount
        unresolvedDiscrepancyCount
        pendingDiscrepancyCount
        encampUnresolved
        encampPending
        encampResolved
        epaUnresolved
        epaPending
        epaResolved
        hasDocumentReview
        documentReviewPending
        documentReviewState
        availableSources
        status
        eManifestComplianceEscalation
        daysSinceShipment
        shippedDate
        isUniformHazardousManifest
        isEManifest
        transporterContactedAt
        exceptionReportSubmissionStatus
        assignee {
          id
          email
          person {
            first
            last
          }
        }
        assignedBy {
          id
          email
          person {
            first
            last
          }
        }
        assignedAt
        documentIds
        commentCount
      }
    }
  }
`);

export const MANIFEST_REVIEW_DISCREPANCIES_QUERY = gql(`
  query ManifestReviewDiscrepancies($processedManifestId: ID!) {
    manifestDiscrepancies(processedManifestId: $processedManifestId) {
      id
      field
      kind
      escalation
      status
      severity
      eManifestValue
      vendorValue
      manifestDocumentValue
    }
  }
`);

// Assignment is manifest-scoped: the server resolves each manifest's CURRENT active
// items (discrepancies + document review) at write time. Sending item ids captured at
// page load instead would fail whenever those items churned underneath an open tab
// (resolved elsewhere, EPA-correction rows deleted, re-seeds) — the "reassign does
// nothing, old assignee remains" bug.
export const ASSIGN_MANIFEST_REVIEW_MANIFESTS_MUTATION = gql(`
  mutation AssignManifestReviewManifestsFromQueue($processedManifestIds: [ID!]!, $staffUserId: ID) {
    assignManifestReviewManifests(processedManifestIds: $processedManifestIds, staffUserId: $staffUserId) {
      assignedDiscrepancyCount
      assignedDocumentReviewCount
    }
  }
`);

export type ManifestReviewRow =
  StaffManifestReviewManifestsQuery["staffManifestReviewManifests"]["items"][number];

export type ManifestReviewDiscrepancy =
  ManifestReviewDiscrepanciesQuery["manifestDiscrepancies"][number];

/** Active discrepancy: not yet resolved or dismissed (mirrors the queue's count). */
export function isActiveDiscrepancy(d: ManifestReviewDiscrepancy): boolean {
  return d.status !== "RESOLVED" && d.status !== "IGNORED";
}

/**
 * Customer-visible discrepancy: anything not still raw-unresolved — i.e. PENDING
 * (in progress), RESOLVED, or IGNORED (done). Customers see these; raw UNRESOLVED
 * field work stays internal to staff review (ENG-15929).
 */
export function isPendingOrResolvedDiscrepancy(d: ManifestReviewDiscrepancy): boolean {
  return d.status !== "UNRESOLVED";
}

/**
 * An in-place "quick link" opened from a queue row instead of navigating to the
 * customer manifest route: the manifest detail drawer, the document-review modal,
 * or the discrepancy dialog on a specific tab. Tenant is carried explicitly since
 * the staff queue is cross-tenant and off any tenant-scoped route.
 */
export type ManifestQuickLink =
  | { kind: "drawer"; processedManifestId: string; tenantId: string }
  | {
      kind: "docReview";
      processedManifestId: string;
      tenantId: string;
      /**
       * Needs-review document ids already on the row. When present we open the
       * document-review modal directly with these, skipping a heavy (uncached)
       * processed-manifest fetch just to resolve the source document.
       */
      documentIds: string[];
    }
  | {
      kind: "discrepancies";
      processedManifestId: string;
      tenantId: string;
      tab: "field" | "compliance";
    };

/** Display name for an assignee (person name, falling back to email). */
export function assigneeLabel(assignee: ManifestReviewRow["assignee"]): string {
  if (!assignee) return "Unassigned";
  const name = assignee.person
    ? `${assignee.person.first ?? ""} ${assignee.person.last ?? ""}`.trim()
    : "";
  return name || assignee.email;
}
